import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import React, { useEffect, useRef } from 'react';
var useLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : function () {};
var activeScope = null;
var scopes = new Set();
export default React.forwardRef(function FocusScope(props, ref) {
  var children = props.children,
    contain = props.contain,
    restoreFocus = props.restoreFocus,
    autoFocus = props.autoFocus,
    modalIds = props.modalIds;
  var startRef = useRef();
  var endRef = useRef();
  var scopeRef = useRef([]);
  useLayoutEffect(function () {
    var node = startRef.current.nextSibling;
    var nodes = [];
    while (node && node !== endRef.current) {
      nodes.push(node);
      node = node.nextSibling;
    }
    scopeRef.current = nodes;
    scopes.add(scopeRef);
    return function () {
      scopes["delete"](scopeRef);
    };
  }, [children]);
  useFocusContainment(scopeRef, contain);
  useRestoreFocus(scopeRef, restoreFocus, contain);
  useAutoFocus(scopeRef, autoFocus, modalIds);
  React.useImperativeHandle(ref, function () {
    return {
      focusFirstInScope: function focusFirstInScope() {
        return _focusFirstInScope(scopeRef.current);
      }
    };
  });
  return React.createElement(React.Fragment, null, React.createElement("span", {
    hidden: true,
    ref: startRef
  }), children, React.createElement("span", {
    hidden: true,
    ref: endRef
  }));
});
var focusableElements = ['input:not([disabled]):not([type=hidden])', 'select:not([disabled])', 'textarea:not([disabled])', 'button:not([disabled])', 'a[href]', 'area[href]', 'summary', 'iframe', 'object', 'embed', 'audio[controls]', 'video[controls]', '[contenteditable]'];
focusableElements.push('[tabindex]:not([tabindex="-1"])');
var TABBABLE_ELEMENT_SELECTOR = focusableElements.join(':not([tabindex="-1"]),');
function getFocusableElementsInScope(scope) {
  var res = [];
  var selector = TABBABLE_ELEMENT_SELECTOR;
  scope.forEach(function (node) {
    if (node.matches(selector)) res.push(node);
    res.push.apply(res, _toConsumableArray(Array.from(node.querySelectorAll(selector))));
  });
  return res;
}
function useFocusContainment(scopeRef, contain) {
  var focusedNode = useRef();
  var raf = useRef(null);
  useEffect(function () {
    var scope = scopeRef.current;
    if (!contain) return;
    var document = getDocument(scope[0]);
    var onKeyDown = function onKeyDown(e) {
      if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey || e.defaultPrevented) return;
      var focusedElement = document.activeElement;
      if (!isElementInScope(focusedElement, scope)) return;
      var elements = getFocusableElementsInScope(scope);
      var position = elements.indexOf(focusedElement);
      var lastPosition = elements.length - 1;
      var nextElement = null;
      if (e.shiftKey) nextElement = elements[position <= 0 ? lastPosition : position - 1];else nextElement = elements[position === lastPosition ? 0 : position + 1];
      e.preventDefault();
      focusElement(nextElement);
    };
    var onFocus = function onFocus(e) {
      var _focusedNode$current;
      var isInAnyScope = isElementInAnyScope(e.target, scopes);
      if (!isInAnyScope) (_focusedNode$current = focusedNode.current) === null || _focusedNode$current === void 0 || _focusedNode$current.focus();else {
        e.stopPropagation();
        activeScope = scopeRef;
        focusedNode.current = e.target;
      }
    };
    var onBlur = function onBlur(e) {
      if (!e.relatedTarget) return;
      var isInAnyScope = isElementInAnyScope(e.relatedTarget, scopes);
      if (!isInAnyScope) {
        activeScope = scopeRef;
        focusedNode.current = e.target;
        raf.current = requestAnimationFrame(function () {
          focusedNode.current.focus();
        });
      }
    };
    document.addEventListener('keydown', onKeyDown, false);
    document.addEventListener('focusin', onFocus, false);
    scope.forEach(function (element) {
      return element.addEventListener('focusin', onFocus, false);
    });
    scope.forEach(function (element) {
      return element.addEventListener('focusout', onBlur, false);
    });
    return function () {
      document.removeEventListener('keydown', onKeyDown, false);
      document.removeEventListener('focusin', onFocus, false);
      scope.forEach(function (element) {
        return element.removeEventListener('focusin', onFocus, false);
      });
      scope.forEach(function (element) {
        return element.removeEventListener('focusout', onBlur, false);
      });
    };
  }, [scopeRef, contain]);
  useEffect(function () {
    return function () {
      return cancelAnimationFrame(raf.current);
    };
  }, []);
}
function isElementInAnyScope(element, _scopes) {
  return Array.from(_scopes.values()).some(function (scope) {
    return isElementInScope(element, scope.current);
  });
}
function isElementInScope(element, scope) {
  return scope.some(function (node) {
    return node.contains(element);
  });
}
function focusElement(element) {
  try {
    element === null || element === void 0 || element.focus();
  } catch (err) {}
}
function getDocument(element) {
  return element ? element.ownerDocument : document;
}
function _focusFirstInScope(scope) {
  var elements = getFocusableElementsInScope(scope);
  focusElement(elements[0]);
}
function useAutoFocus(scopeRef, autoFocus, modalIds) {
  useEffect(function () {
    if (!autoFocus && modalIds) {
      var _document = getDocument();
      var focusableModalElements = modalIds.map(function (id) {
        return _document.getElementById(id);
      });
      var firstFocusableModalElement = focusableModalElements.find(function (element) {
        return element;
      });
      if (firstFocusableModalElement) {
        firstFocusableModalElement.setAttribute('tabindex', '0');
        firstFocusableModalElement.focus();
      }
      return;
    }
    if (!autoFocus) {
      return;
    }
    activeScope = scopeRef;
    var document = getDocument(activeScope.current[0]);
    if (isElementInScope(document.activeElement, activeScope.current)) return;
    _focusFirstInScope(scopeRef.current);
  }, [scopeRef, autoFocus, modalIds]);
}
function useRestoreFocus(scopeRef, restoreFocus, contain) {
  useLayoutEffect(function () {
    var scope = scopeRef.current;
    var document = getDocument(scope[0]);
    var nodeToRestore = document.activeElement;
    var onKeyDown = function onKeyDown(e) {
      if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
      var focusedElement = document.activeElement;
      if (!isElementInScope(focusedElement, scope)) return;
      var walker = getFocusableTreeWalker(document.body);
      walker.currentNode = focusedElement;
      var nextElement = e.shiftKey ? walker.previousNode() : walker.nextNode();
      if ((!nextElement || !isElementInScope(nextElement, scope)) && nodeToRestore) {
        walker.currentNode = nodeToRestore;
        do nextElement = e.shiftKey ? walker.currentNode : walker.nextNode(); while (isElementInScope(nextElement, scope) && !e.shiftKey);
        e.preventDefault();
        e.stopPropagation();
        if (nextElement) nextElement.focus();else {
          focusedElement.blur();
        }
      }
    };
    if (!contain) document.addEventListener('keydown', onKeyDown, true);
    return function () {
      if (!contain) document.removeEventListener('keydown', onKeyDown, true);
      if (restoreFocus && nodeToRestore && isElementInScope(document.activeElement, scope)) {
        requestAnimationFrame(function () {
          if (document.body.contains(nodeToRestore)) focusElement(nodeToRestore);
        });
      }
    };
  }, [scopeRef, restoreFocus, contain]);
}
function getFocusableTreeWalker(root) {
  var selector = TABBABLE_ELEMENT_SELECTOR;
  var walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
    acceptNode: function acceptNode(node) {
      if (node.matches(selector)) return NodeFilter.FILTER_ACCEPT;
      return NodeFilter.FILTER_SKIP;
    }
  }, false);
  return walker;
}